import React, { useState, useEffect } from 'react';
import { TopicClustersResult, TopicCluster } from '../services/topicClusters';

interface TopicClustersPanelProps {
  clustersResult: TopicClustersResult | null;
  onMethodChange: (method: 'word-based' | 'semantic-similarity' | 'page-based') => void;
  onFilterByCluster: (clusterKeywords: string[]) => void;
  isLoading?: boolean;
  propertyUrl?: string;
}

// Gradient SVG Icons Component (reused from InsightsPanel)
const GradientIcon: React.FC<{ type: string; size?: string }> = ({ type, size = "w-5 h-5" }) => {
  const iconPaths: { [key: string]: string } = {
    cluster: "M19 11H5m14-7H5m14 14H5m14-7H5M5 5l14 14M5 19L19 5",
    expand: "M19 9l-7 7-7-7",
    collapse: "M5 15l7-7 7 7",
    filter: "M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z",
    pages: "M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z",
    refresh: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
  };

  return (
    <svg className={size} viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id={`gradient-${type}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ec4899" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      <path
        d={iconPaths[type] || iconPaths.cluster}
        stroke={`url(#gradient-${type})`}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const ClusterCard: React.FC<{
  cluster: TopicCluster;
  onFilterByCluster: (clusterKeywords: string[]) => void;
  propertyUrl?: string;
}> = ({ cluster, onFilterByCluster, propertyUrl }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [contentBrief, setContentBrief] = useState<string | null>(null);
  const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
  const [isLoadingBrief, setIsLoadingBrief] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Load saved brief on mount
  useEffect(() => {
    const loadSavedBrief = async () => {
      if (!propertyUrl) {
        setIsLoadingBrief(false);
        return;
      }

      try {
        const response = await fetch(
          `/api/topic-cluster-briefs/${encodeURIComponent(propertyUrl)}/${encodeURIComponent(cluster.name)}`,
          { credentials: 'include' }
        );

        if (response.ok) {
          const data = await response.json();
          setContentBrief(data.brief);
        }
      } catch (error) {
        console.error('Error loading saved brief:', error);
      } finally {
        setIsLoadingBrief(false);
      }
    };

    loadSavedBrief();
  }, [propertyUrl, cluster.name]);

  const generateContentBrief = async () => {
    if (!propertyUrl) {
      alert('Property URL is required');
      return;
    }

    setIsGeneratingBrief(true);
    try {
      // Prepare cluster data for the AI
      const topKeywords = cluster.keywords
        .sort((a, b) => b.clicks - a.clicks)
        .slice(0, 10)
        .map(k => `${k.query} (pos: ${k.position.toFixed(1)}, clicks: ${k.clicks})`)
        .join('\n');

      const prompt = `Create a comprehensive content brief for an SEO content cluster:

Topic Cluster: ${cluster.name}
Total Keywords: ${cluster.keywords.length}
Performance: ${cluster.totalClicks} clicks, ${cluster.totalImpressions} impressions, Avg Position ${cluster.averagePosition.toFixed(1)}

Top Performing Keywords:
${topKeywords}

Top Ranking Pages:
${cluster.topPages.slice(0, 3).join('\n')}

Current Issues:
${cluster.recommendations.join('\n')}

Please provide:
1. Primary Topic & Search Intent
2. Target Audience 
3. Key Points to Cover (based on keywords)
4. Recommended Content Structure (H2/H3 headings)
5. Internal Linking Opportunities (based on top pages)
6. Optimization Tips (based on current performance)

Keep it concise and actionable (max 400 words).`;

      const response = await fetch('/api/generate-content-brief', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt })
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (jsonError) {
          // If we can't parse the error response, show upgrade modal for free users
          setShowUpgradeModal(true);
          return;
        }
        
        // Check if this is a premium feature restriction
        if (errorData.upgradeRequired) {
          setShowUpgradeModal(true);
          return;
        }
        
        throw new Error(errorData.message || 'Failed to generate content brief');
      }
      
      const data = await response.json();
      
      // Save the brief to database
      const saveResponse = await fetch('/api/topic-cluster-briefs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          propertyUrl,
          clusterKey: cluster.name,
          brief: data.brief
        })
      });

      if (!saveResponse.ok) {
        console.error('Failed to save brief to database');
      }

      setContentBrief(data.brief);
    } catch (error) {
      console.error('Error generating content brief:', error);
      alert('Failed to generate content brief. Please try again.');
    } finally {
      setIsGeneratingBrief(false);
    }
  };

  const exportBrief = () => {
    if (!contentBrief) return;

    // Create a text file with the brief
    const briefContent = `Content Brief: ${cluster.name}\n\n${contentBrief}`;
    const blob = new Blob([briefContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    // Create a download link and trigger it
    const link = document.createElement('a');
    link.href = url;
    link.download = `content-brief-${cluster.name.replace(/\s+/g, '-').toLowerCase()}.txt`;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDeleteBrief = async () => {
    if (!propertyUrl) return;

    try {
      // Delete from database
      const response = await fetch(
        `/api/topic-cluster-briefs/${encodeURIComponent(propertyUrl)}/${encodeURIComponent(cluster.name)}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        console.error('Failed to delete brief from database');
      }
    } catch (error) {
      console.error('Error deleting brief:', error);
    } finally {
      // Remove from UI regardless of API success
      setContentBrief(null);
      setShowDeleteConfirm(false);
    }
  };

  // Get health score color
  const getHealthColor = (grade: string) => {
    switch (grade) {
      case 'A': return 'text-green-600 bg-green-50 border-green-200';
      case 'B': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'C': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'D': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'F': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Get priority badge styling
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-gray-100 text-gray-700 border-gray-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="p-4">
        {/* Cluster Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate mb-1" title={cluster.name}>
              {cluster.name}
            </h3>
            <p className="text-sm text-gray-600">
              {cluster.keywords.length} keywords • {cluster.topPages.length} pages
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={() => onFilterByCluster(cluster.keywords.map(k => k.query))}
              className="text-xs bg-blue-100 text-blue-600 px-3 py-1 rounded hover:bg-blue-200 transition-colors flex items-center gap-1"
              title="Filter main table by this cluster"
            >
              <GradientIcon type="filter" size="w-3 h-3" />
              Filter
            </button>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded hover:bg-gray-200 transition-colors flex items-center gap-1"
            >
              <GradientIcon type={isExpanded ? "collapse" : "expand"} size="w-3 h-3" />
              {isExpanded ? 'Less' : 'More'}
            </button>
          </div>
        </div>

        {/* Cluster Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-xl font-bold text-blue-600">{cluster.totalClicks.toLocaleString()}</div>
            <div className="text-xs text-blue-800">Total Clicks</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <div className="text-xl font-bold text-green-600">{cluster.totalImpressions.toLocaleString()}</div>
            <div className="text-xs text-green-800">Impressions</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-3">
            <div className="text-xl font-bold text-purple-600">{cluster.averagePosition.toFixed(1)}</div>
            <div className="text-xs text-purple-800">Avg Position</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3">
            <div className="text-xl font-bold text-yellow-600">{(cluster.averageCTR * 100).toFixed(1)}%</div>
            <div className="text-xs text-yellow-800">Avg CTR</div>
          </div>
        </div>

        {/* Top Pages */}
        <div className="mb-3">
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <GradientIcon type="pages" size="w-4 h-4" />
            Top Pages
          </h4>
          <div className="space-y-1">
            {cluster.topPages.slice(0, 3).map((page, index) => (
              <div key={index} className="text-xs text-gray-600 truncate bg-gray-50 px-2 py-1 rounded">
                {page}
              </div>
            ))}
          </div>
        </div>

        {/* Actionable Recommendations */}
        <div className="mb-3">
          <button
            onClick={() => setShowRecommendations(!showRecommendations)}
            className="w-full text-left text-sm font-medium text-gray-700 mb-2 flex items-center justify-between hover:text-gray-900"
          >
            <span className="flex items-center gap-1">
              💡 Recommendations ({cluster.recommendations.length})
            </span>
            <span className="text-xs">{showRecommendations ? '▲' : '▼'}</span>
          </button>
          {showRecommendations && (
            <div className="space-y-2 bg-blue-50 p-3 rounded-lg">
              {cluster.recommendations.map((rec, index) => (
                <div key={index} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">•</span>
                  <span className="flex-1">{rec}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Content Brief Generator */}
        <div className="mb-3">
          <button
            onClick={generateContentBrief}
            disabled={isGeneratingBrief || !!contentBrief}
            className={`w-full text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
              contentBrief 
                ? 'bg-green-100 text-green-700 border border-green-300 cursor-default'
                : isGeneratingBrief
                ? 'bg-gray-200 text-gray-500 cursor-wait'
                : 'bg-purple-100 text-purple-700 border border-purple-300 hover:bg-purple-200'
            }`}
          >
            {isGeneratingBrief ? '✨ Generating Brief...' : contentBrief ? '✅ Content Brief Generated' : '📝 Generate Content Brief'}
          </button>
          
          {contentBrief && (
            <div className="mt-3 bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-purple-900">AI Content Brief (Auto-saved)</h4>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportBrief}
                    className="text-xs px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                  >
                    📥 Export Brief
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-xs text-purple-600 hover:text-purple-800"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-700 whitespace-pre-wrap">{contentBrief}</div>
              
              {/* Delete Confirmation Dialog */}
              {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Remove Content Brief?</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Are you sure you want to remove this brief? It will be permanently deleted from your saved briefs.
                    </p>
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteBrief}
                        className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      >
                        Remove Brief
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Upgrade Modal for Premium Features */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  ✨ Premium Feature
                </h3>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  <strong>AI Content Briefs</strong> are available on the Pro plan.
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Upgrade to unlock AI-powered content briefs that analyze your topic clusters and provide actionable recommendations for content creation.
                </p>
                
                <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-4 rounded-lg border border-purple-200">
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">Pro Plan Includes:</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500">✓</span>
                      <span>Unlimited GSC properties</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500">✓</span>
                      <span>16 months of historical data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500">✓</span>
                      <span>AI Content Briefs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500">✓</span>
                      <span>Full Topic Clusters analysis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500">✓</span>
                      <span>Advanced analytics & insights</span>
                    </li>
                  </ul>
                  <div className="mt-3 text-center">
                    <p className="text-2xl font-bold text-purple-900">£15/month</p>
                    <p className="text-xs text-purple-700">inc VAT</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Maybe Later
                </button>
                <a
                  href="/account"
                  className="flex-1 px-4 py-2 text-sm bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-600 hover:to-purple-700 transition-colors text-center font-semibold"
                >
                  Upgrade to Pro
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Expanded Keywords List */}
        {isExpanded && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Keywords in this cluster</h4>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {cluster.keywords
                .sort((a, b) => b.clicks - a.clicks)
                .slice(0, 20)
                .map((keyword, index) => (
                  <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded text-xs">
                    <div className="flex-1 min-w-0 mr-3">
                      <div className="font-medium text-gray-900 truncate" title={keyword.query}>
                        {keyword.query}
                      </div>
                      <div className="text-gray-500 truncate" title={keyword.page || 'No page data'}>
                        {keyword.page || 'No page data'}
                      </div>
                    </div>
                    <div className="flex gap-4 text-right">
                      <div>
                        <div className="text-blue-600 font-medium">{keyword.clicks}</div>
                        <div className="text-gray-500">clicks</div>
                      </div>
                      <div>
                        <div className="text-green-600 font-medium">{keyword.impressions}</div>
                        <div className="text-gray-500">impr</div>
                      </div>
                      <div>
                        <div className="text-purple-600 font-medium">{keyword.position.toFixed(1)}</div>
                        <div className="text-gray-500">pos</div>
                      </div>
                    </div>
                  </div>
                ))}
              {cluster.keywords.length > 20 && (
                <div className="text-xs text-gray-600 text-center py-2">
                  ... and {cluster.keywords.length - 20} more keywords, see all the keywords by using the{' '}
                  <a href="#query-performance" className="text-blue-600 hover:text-blue-800 underline">
                    Query Performance tool
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TopicClustersPanel: React.FC<TopicClustersPanelProps> = ({
  clustersResult,
  onMethodChange,
  onFilterByCluster,
  isLoading = false,
  propertyUrl
}) => {
  const [selectedMethod, setSelectedMethod] = useState<'word-based' | 'semantic-similarity' | 'page-based'>('word-based');

  const handleMethodChange = (method: 'word-based' | 'semantic-similarity' | 'page-based') => {
    setSelectedMethod(method);
    onMethodChange(method);
  };

  if (!clustersResult) {
    return (
      <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-gray-200 p-6 mb-6">
        <div className="text-center">
          <div className="mb-2 inline-block">
            <GradientIcon type="cluster" size="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mt-2">Topic Clusters</h2>
          <p className="text-gray-600 mt-1">Load your SEO data to discover topic clusters and keyword themes.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-gray-200 p-6 mb-6">
        <div className="text-center">
          <div className="mb-2 inline-block animate-spin">
            <GradientIcon type="refresh" size="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mt-2">Analyzing Topic Clusters</h2>
          <p className="text-gray-600 mt-1">Processing your keywords to identify topic clusters...</p>
        </div>
      </div>
    );
  }

  if (clustersResult.clusters.length === 0) {
    return (
      <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <GradientIcon type="cluster" size="w-6 h-6" />
              Topic Clusters
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              No clusters found with the current method and minimum size threshold.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Method:</label>
            <select
              value={selectedMethod}
              onChange={(e) => handleMethodChange(e.target.value as any)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="word-based">Word-Based</option>
              <option value="semantic-similarity">Semantic Similarity</option>
              <option value="page-based">Page-Based</option>
            </select>
          </div>
        </div>
        <div className="text-center py-8">
          <p className="text-gray-600">
            Try a different clustering method or ensure you have enough data (10+ keywords per topic).
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-gray-200 p-6 mb-6">
      {/* Header with Method Selector */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <GradientIcon type="cluster" size="w-6 h-6" />
            Topic Clusters
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            Found {clustersResult.totalClusters} clusters with {clustersResult.totalKeywordsInClusters} keywords
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Clustering Method:</label>
          <select
            value={selectedMethod}
            onChange={(e) => handleMethodChange(e.target.value as any)}
            className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="word-based">Word-Based</option>
            <option value="semantic-similarity">Semantic Similarity</option>
            <option value="page-based">Page-Based</option>
          </select>
        </div>
      </div>

      {/* Method Description */}
      <div className="mb-4 p-3 bg-white/60 rounded-lg">
        <p className="text-sm text-gray-700">
          {selectedMethod === 'word-based' && '📝 Groups keywords sharing common root words and phrases'}
          {selectedMethod === 'semantic-similarity' && '🧠 Groups conceptually related keywords by topic themes'}  
          {selectedMethod === 'page-based' && '📄 Groups keywords by the pages they rank for'}
        </p>
      </div>

      {/* Clusters Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {clustersResult.clusters.map((cluster) => (
          <ClusterCard
            key={cluster.id}
            cluster={cluster}
            onFilterByCluster={onFilterByCluster}
            propertyUrl={propertyUrl}
          />
        ))}
      </div>

      {/* Summary Footer */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="text-center text-sm text-gray-600">
          <p>
            Showing {clustersResult.clusters.length} topic clusters • 
            {clustersResult.totalKeywordsInClusters} keywords clustered • 
            Minimum {10} keywords per cluster
          </p>
        </div>
      </div>
    </div>
  );
};

export default TopicClustersPanel;